import {
  Alert,
  Container,
  Header,
  Spinner,
} from '@amzn/awsui-components-react';
import { ApolloClient } from '@apollo/client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { FeedbackForm, SubmittedDraft } from './FeedbackForm';
import './MerlinFeedback.css';
import {
  PAGE_ANALYSIS_NOT_FOUND_MESSAGE,
  PAGE_ANALYSIS_UNAVAILABLE_MESSAGE,
  PAGE_EXPIRED_LINK_MESSAGE,
  PAGE_INVALID_LINK_MESSAGE,
  PAGE_NOT_AUTHORIZED_MESSAGE,
  PAGE_SKIPPED_MESSAGE,
  pageLoadingMessage,
  pageMissingParameterMessage,
} from './constants';
import { DeepLinkResult, parseDeepLink } from './deepLink';
import { useFeedbackContext, ResolveContext } from './hooks/useFeedbackContext';
import { useSubmitFeedback } from './hooks/useSubmitFeedback';
import {
  ContextResolutionOutcome,
  MerlinFeedbackTelemetry,
  PageOutcomeEvent,
  noopTelemetry,
} from './pageOutcomeMetric';

/**
 * The standalone ticket Feedback_Page — **one surface, two pre-set Ratings.**
 *
 * Both values of `r` render the same `FeedbackForm`, differing only in the pre-set Rating. There is no
 * separate confirmation surface for `r=up`: rendering the full form on the positive branch is what makes
 * field 2 and field 4 available there, which is the point.
 */

export interface FeedbackPageParams {
  clientId: string;
  domainName: string;
  ticketId: string;
}

export interface FeedbackPageProps
  extends RouteComponentProps<FeedbackPageParams> {
  client: ApolloClient<object>;
  resolveContext: ResolveContext;
  telemetry?: MerlinFeedbackTelemetry;
  /** Injected so every outcome is a pure function of the six parameters and this clock. */
  now?: Date;
  /**
   * Open Decision (e) is unresolved and this page sits outside the `authorizedUsers` list, so gating
   * here would lock out every recipient of a ticket link. Kept as one predicate in one place so
   * switching it on, off, or onto a different list is a one-line change.
   */
  isAuthorized?: boolean;
}

/** What the page settles on. `loading` is a transient, not a sixth outcome. */
type PageState =
  | { kind: 'loading'; ticketId: string }
  | { kind: 'renderable' }
  | { kind: 'named-missing-parameter'; parameterName: string }
  | { kind: 'invalid-link' }
  | { kind: 'expired-link' }
  | { kind: 'analysis-not-found' }
  | { kind: 'unavailable' }
  | { kind: 'not-authorized' };

export const FeedbackPage: React.FC<FeedbackPageProps> = ({
  match,
  location,
  client,
  resolveContext,
  telemetry = noopTelemetry,
  now,
  isAuthorized = true,
}) => {
  const { clientId, domainName, ticketId } = match.params;

  // `now` is captured once so a re-render cannot move a link across its own expiry boundary mid-session.
  const nowRef = useRef(now ?? new Date());

  const parsed: DeepLinkResult = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return parseDeepLink(
      {
        clientId,
        domainName,
        ticketId,
        v: query.get('v'),
        r: query.get('r'),
        sessionId: query.get('sessionId'),
      },
      nowRef.current
    );
  }, [clientId, domainName, ticketId, location.search]);

  const link = parsed.outcome === 'renderable' ? parsed.link : null;

  // The read runs only for a link that parsed. Reading on a rejected link would spend an AgentCore call
  // to answer a question the parser has already settled.
  const context = useFeedbackContext(
    'TICKET',
    // Always the **derived** session id. The carried parameter was only ever compared, in the parser.
    link ? link.sessionId : '',
    resolveContext,
    {
      ticketId,
      domainIdentifier: `${clientId}:${domainName}`,
      enabled: link !== null && isAuthorized,
    }
  );

  const state: PageState = useMemo(() => {
    if (!isAuthorized) return { kind: 'not-authorized' };

    switch (parsed.outcome) {
      case 'named-missing-parameter':
        return {
          kind: 'named-missing-parameter',
          parameterName: parsed.missing,
        };
      case 'invalid-link':
        return { kind: 'invalid-link' };
      case 'expired-link':
        return { kind: 'expired-link' };
      case 'renderable':
        break;
    }

    // A parsed link still has to wait for the read, because that one read is also the existence check.
    // Rendering the form now and withdrawing it on `resolvedEmpty` would be worse than waiting: by then
    // a record can already have been written, and the check would only be as strong as the user's
    // typing speed.
    if (context.state.status === 'loading')
      return { kind: 'loading', ticketId };

    // A proven absence is permanent; the form must never render.
    if (context.isAnalysisMissing) return { kind: 'analysis-not-found' };

    /**
     * Without an `eventId` there is nothing to render.
     *
     * Every Feedback_Record now names the memory event it rates, for both chat and tickets, so a rating
     * that cannot name one cannot be stored. The check belongs here rather than at submit: a form that
     * will certainly be refused is worse than no form, because the user writes the free text first.
     *
     * This is fail-closed, reversing the earlier policy, and the tradeoff is real. `empty` is a proven
     * absence, but `error` and `timedout` are merely unknown, and both now refuse — so a memory outage
     * suppresses ticket feedback fleet-wide rather than storing unattributed ratings. That is the price of
     * the guarantee that every record is traceable to an analysis.
     *
     * They refuse under a *different* state, though. Both decline the form, but only `resolvedEmpty` has
     * earned the right to tell the user the analysis does not exist; a failed read reports that it could
     * not be loaded and is worth retrying. Collapsing the two would make an outage look permanent.
     */
    if (!context.eventId) return { kind: 'unavailable' };
    return { kind: 'renderable' };
  }, [
    isAuthorized,
    parsed,
    context.state.status,
    context.isAnalysisMissing,
    context.eventId,
    ticketId,
  ]);

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const readStartedAtRef = useRef<number | null>(null);
  if (readStartedAtRef.current === null && link !== null) {
    readStartedAtRef.current = Date.now();
  }

  const emittedContextRef = useRef(false);
  useEffect(() => {
    if (emittedContextRef.current) return;
    const outcome = contextOutcomeFor(context.state.status);
    if (outcome === null) return; // still loading
    emittedContextRef.current = true;
    telemetry.emitContextResolution({
      outcome,
      durationMs:
        readStartedAtRef.current === null
          ? 0
          : Date.now() - readStartedAtRef.current,
    });
  }, [context.state.status, telemetry]);

  const emittedOutcomeRef = useRef(false);
  useEffect(() => {
    // Emitted once per page, on the settled outcome. `loading` is not settled, and `not-authorized` is
    // not one of the five — emitting it would put a value outside the closed set on the dimension.
    if (emittedOutcomeRef.current) return;
    if (state.kind === 'loading' || state.kind === 'not-authorized') return;
    emittedOutcomeRef.current = true;
    telemetry.emitPageOutcome(pageOutcomeEventFor(state, parsed));
  }, [state, parsed, telemetry]);

  // ─── Submission ────────────────────────────────────────────────────────────

  const { formOpenId, submit } = useSubmitFeedback(client);

  const onSubmit = useCallback(
    async (draft: SubmittedDraft): Promise<string> => {
      if (link === null) {
        // Unreachable: the form renders only on a parsed link. Throwing rather than silently resolving
        // keeps a future refactor from turning this into a submission that goes nowhere.
        throw new Error('Cannot submit feedback for an unresolved deep link');
      }
      const result = await submit({
        triggerPoint: 'TICKET',
        rating: draft.rating,
        whatShouldItHaveSaid: draft.whatShouldItHaveSaid,
        whatDidWeGetWrong: draft.whatDidWeGetWrong,
        wrongPartTags: draft.wrongPartTags,
        domainIdentifier: `${link.clientId}:${link.domainName}`,
        sessionId: link.sessionId,
        ticketId: link.ticketId,
        deepLinkVersion: link.version,
        // Carried into `feedbackDetails` when the read found one, omitted when the existence check was
        // indeterminate. It is never part of the ticket record's key — putting it there would break the
        // per-user-per-ticket override.
        eventId: context.eventId,
      });
      return result.feedbackRecordId;
    },
    [link, submit, context.eventId]
  );

  /**
   * Skip replaces the form with a closing message.
   *
   * There is no modal to dismiss here and no page to navigate back to — the reader arrived from a link
   * in a ticket. Leaving the form on screen made Skip look broken: nothing moved, so the natural reading
   * was that the click had failed and the only way out was to close the tab and wonder whether a rating
   * had been recorded. Replacing the form is the smallest change that makes the button observably do
   * something, and it states plainly that nothing was stored.
   *
   * `window.close()` is deliberately not attempted: browsers only honour it for script-opened windows,
   * so it would work for nobody arriving from a ticket link and silently do nothing.
   */
  const [dismissed, setDismissed] = useState(false);
  const onSkip = useCallback(() => setDismissed(true), []);

  // ─── Render ────────────────────────────────────────────────────────────────

  // Ahead of the switch: a dismissal is the reader's own decision and outranks whatever the link and
  // the context read concluded. Nothing was written, so there is no outcome to reconcile.
  if (dismissed) {
    return <PageMessage type="info" text={PAGE_SKIPPED_MESSAGE} />;
  }

  switch (state.kind) {
    case 'not-authorized':
      return <PageMessage type="error" text={PAGE_NOT_AUTHORIZED_MESSAGE} />;
    case 'named-missing-parameter':
      return (
        <PageMessage
          type="error"
          text={pageMissingParameterMessage(state.parameterName)}
        />
      );
    case 'invalid-link':
      return <PageMessage type="error" text={PAGE_INVALID_LINK_MESSAGE} />;
    case 'expired-link':
      return <PageMessage type="warning" text={PAGE_EXPIRED_LINK_MESSAGE} />;
    case 'analysis-not-found':
      return <PageMessage type="info" text={PAGE_ANALYSIS_NOT_FOUND_MESSAGE} />;
    // `warning`, not `info`: unlike a proven absence this is a fault, and one the user can act on.
    case 'unavailable':
      return (
        <PageMessage type="warning" text={PAGE_ANALYSIS_UNAVAILABLE_MESSAGE} />
      );
    case 'loading':
      return (
        <div className="mfb-page">
          <div className="mfb-card" data-testid="merlin-page-loading">
            <Container>
              <div className="mfb-loading">
                <Spinner />
                {/* Named and explanatory, not a bare spinner: the wait can reach a minute, and at
                    that length a spinner alone reads as a broken page. */}
                <span>{pageLoadingMessage(state.ticketId)}</span>
              </div>
            </Container>
          </div>
        </div>
      );
    case 'renderable':
      return (
        <div className="mfb-page">
          <div className="mfb-card" data-testid="merlin-feedback-page">
            {/* A card, so the form reads as one self-contained task on an otherwise empty page. */}
            <Container>
              <FeedbackForm
                contextSubtitle={contextSubtitleFor(
                  context.state,
                  ticketId,
                  domainName
                )}
                initialRating={link ? link.presetRating : null}
                formOpenId={formOpenId}
                onSubmit={onSubmit}
                onSkip={onSkip}
              />
            </Container>
          </div>
        </div>
      );
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Every non-renderable outcome uses this one shell, so they cannot drift apart in layout.
 *
 * No retry control: every remaining non-renderable outcome is a settled fact about the link or the
 * analysis, and offering to re-run one would invite the user to retry something that cannot change.
 */
const PageMessage: React.FC<{
  type: 'error' | 'warning' | 'info';
  text: string;
}> = ({ type, text }) => (
  <div className="mfb-page">
    <div className="mfb-message">
      <Alert type={type} data-testid="merlin-page-message">
        {text}
      </Alert>
    </div>
  </div>
);

/** `null` while the read is outstanding — there is nothing settled to report yet. */
export function contextOutcomeFor(
  status: string
): ContextResolutionOutcome | null {
  switch (status) {
    case 'resolved':
      return 'events';
    case 'resolvedEmpty':
      return 'empty';
    case 'failed':
      return 'error';
    case 'timedout':
      return 'timed-out';
    default:
      return null;
  }
}

/**
 * The `cause` reaches the metric here, and only here.
 *
 * A distinction that exists only in the parser's return type does not satisfy criterion 12b: the signal
 * is a rate, so it has to be emitted. In particular `sessionId-mismatch` must arrive as its own value —
 * one mismatch is a bad link, but every link mismatching at once means our derivation has drifted from
 * the interceptor's, and that pattern is only visible if the cause is not collapsed into a generic
 * invalid-link.
 */
export function pageOutcomeEventFor(
  state: PageState,
  parsed: DeepLinkResult
): PageOutcomeEvent {
  switch (state.kind) {
    case 'invalid-link':
      return {
        outcome: 'invalid-link',
        cause: parsed.outcome === 'invalid-link' ? parsed.cause : undefined,
      };
    case 'named-missing-parameter':
      return {
        outcome: 'named-missing-parameter',
        missingParameter: state.parameterName,
      };
    case 'expired-link':
      return { outcome: 'expired-link' };
    case 'analysis-not-found':
      return { outcome: 'analysis-not-found' };
    case 'unavailable':
      return { outcome: 'analysis-unavailable' };
    // Emitted on the happy path too, deliberately: it is the denominator that makes the other outcomes
    // rates rather than bare counts.
    default:
      return { outcome: 'renderable' };
  }
}

/**
 * Rendered on **both** branches of `r`. The subtitle is a property of the ticket Trigger_Point rather
 * than of the form — it is what tells the user which analysis they are commenting on.
 *
 * `ticketId` and `domainName` come from the **deep link**, not from the context read. They are in the URL
 * path, so they are available before the read settles and remain available when it fails — which is why
 * even the unresolved branch can still name what the form refers to.
 */
function contextSubtitleFor(
  state: ReturnType<typeof useFeedbackContext>['state'],
  ticketId: string,
  domainName: string
): React.ReactNode {
  const identity = `ticket ${ticketId} · ${domainName}`;
  if (state.status === 'resolved') {
    const analysedAt = formatAnalysedAt(state.analysedAt);
    return (
      <Header variant="h3">
        {analysedAt ? `${identity} · analysed ${analysedAt}` : identity}
      </Header>
    );
  }
  // The unresolved-context marker. Existence is unknown here, which is why the form is rendering at
  // all — so the subtitle says what it does not know rather than implying the analysis is missing.
  return (
    <Header variant="h3">{`${identity} · analysis details unavailable`}</Header>
  );
}

/** Falls back to the raw value rather than hiding it, so an unexpected format is visible not silent. */
function formatAnalysedAt(value: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toUTCString();
}
